import { Hierarchy } from "@models/hierarchy";
import { SiteDto } from "@models/site-data";
import { SiteGroup } from "@models/site-group";
import { MySitesDto } from "@models/site-with-module-status";
import { transformHierarchy } from "@services/hierarchy-transformer";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "../axios-instance";

type UseGetAllowedSitesApiOptions = {
  includeRootEquipments?: boolean;
  includeSiteDetails?: boolean;
};

export function useGetAllowedSitesApi(
  options: UseGetAllowedSitesApiOptions = {}
) {
  const includeRootEquipments = options.includeRootEquipments ?? false;
  const includeSiteDetails = options.includeSiteDetails ?? false;

  return useQuery<MySitesDto>({
    queryKey: ["sites", includeRootEquipments, includeSiteDetails],
    queryFn: async (): Promise<MySitesDto> => {
      const params = new URLSearchParams();
      if (includeRootEquipments) {
        params.set("includeRootEquipments", "true");
      }
      if (includeSiteDetails) {
        params.set("includeSiteDetails", "true");
      }

      const query = params.toString();
      const url = query ? `/sites/my-sites?${query}` : `/sites/my-sites`;
      const response = (await axiosInstance.get<MySitesDto>(url))
        .data as MySitesDto;

      return {
        ...response,
        sites: response.sites.sort((s1, s2) => s1.site.localeCompare(s2.site)),
      };
    },
    staleTime: Infinity,
  });
}

export function useGetSiteApi(
  site?: string,
  enabled: boolean = true,
  productionArea?: string
) {
  const queryClient = useQueryClient();

  return useQuery<SiteDto>({
    queryKey:
      productionArea === undefined
        ? [site, "site"]
        : [site, "site", productionArea],
    queryFn: async (): Promise<SiteDto> => {
      const params = new URLSearchParams();
      if (productionArea !== undefined) {
        params.set("productionArea", productionArea);
      }
      const query = params.toString();
      const url = `/sites/${encodeURIComponent(site!)}${query ? `?${query}` : ""}`;
      return (await axiosInstance.get<SiteDto>(url)).data as SiteDto;
    },
    staleTime: Infinity,
    initialData: () => {
      if (productionArea !== undefined) return undefined;
      const hierarchy = queryClient.getQueryData<Hierarchy>(["hierarchy"]);
      if (!site || !hierarchy) return undefined;
      const siteGroups = transformHierarchy(hierarchy);
      return findSiteInHierarchy(siteGroups, site);
    },
    enabled: !!site && enabled,
  });
}

function findSiteInHierarchy(
  hierarchy: SiteGroup[],
  site: string
): SiteDto | undefined {
  const s = hierarchy.flatMap((sg) => sg.plants).find((s) => s.name === site);

  if (s === undefined) return undefined;

  return {
    orgFullName: s.fullName,
    orgShortName: s.name,
    productionArea: s.productionArea,
    projectOverviewReport: s.projectOverviewReport,
    systemOverviewReport:
      s.rootEquipment.length > 0 ? s.rootEquipment[0].defaultReport : "",
    rootEquipments: s.rootEquipment.map((re) => re.name),
  } as SiteDto;
}
